import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { editSocialLinks, profileDetails } from "../../services/user.service";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { handleServerValidations } from "../../../../utils/commonfunction";

const SOCIAL_PLATFORMS = [
  { key: "facebook", label: "Facebook", icon: "/images/f01.png", placeholder: "https://facebook.com/yourprofile" },
  { key: "instagram", label: "Instagram", icon: "/images/f03.png", placeholder: "https://instagram.com/yourprofile" },
  { key: "twitch", label: "Twitch", icon: "/images/twitch.png", placeholder: "https://twitch.tv/yourprofile" },
  // { key: "linkedin", label: "LinkedIn", icon: "/images/linkdin.png", placeholder: "https://linkedin.com/in/yourprofile" },
  { key: "youtube", label: "Youtube", icon: "/images/f02.png", placeholder: "https://youtube.com/yourchannel" },
  // { key: "pinterest", label: "Pinterest", icon: "/images/f03.png", placeholder: "https://pinterest.com/yourprofile" },
  // { key: "website", label: "Website", icon: "/images/globe.svg", placeholder: "https://yourwebsite.com" },
];

function SocialLinks() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [initialLinks, setInitialLinks] = useState({
    facebook: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    telegram: "",
    pinterest: "",
    website: "",
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await profileDetails();
        if (res?.data?.social_links) {
          setInitialLinks({
            facebook: res.data.social_links.facebook || "",
            instagram: res.data.social_links.instagram || "",
            twitter: res.data.social_links.twitter || "",
            youtube: res.data.social_links.youtube || "",
            twitch: res.data.social_links.twitch || "",
            pinterest: res.data.social_links.pinterest || "",
            website: res.data.social_links.website || "",
          });
        }
      } catch (err) {
        console.error("Failed to fetch social links:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const validationSchema = Yup.object().shape({
    facebook: Yup.string().url(t("vaildation.kyc_social_media.url")).nullable(),
    instagram: Yup.string().url(t("vaildation.kyc_social_media.url")).nullable(),
    twitter: Yup.string().url(t("vaildation.kyc_social_media.url")).nullable(),
    linkedin: Yup.string().url(t("vaildation.kyc_social_media.url")).nullable(),
    telegram: Yup.string().url(t("vaildation.kyc_social_media.url")).nullable(),
    pinterest: Yup.string().url(t("vaildation.kyc_social_media.url")).nullable(),
    website: Yup.string().url(t("vaildation.kyc_social_media.url")).nullable(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await editSocialLinks({ social_links: values });

      if (response.success) {
        Swal.fire({
          icon: "success",
          text: response.message || t("social_links.success"),
          ...SWAL_SETTINGS,
        });
      } else {
        Swal.fire({
          icon: "error",
          text: handleServerValidations(response),
          ...SWAL_SETTINGS,
        });
      }
    } catch (error) {
      handleServerValidations(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-10 text-center">{t("loading")}</div>;

  return (
    <div className="max-w-[100%] mx-auto">
      <Formik
        initialValues={initialLinks}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="user-profile-bg bg-primary-50 p-[15px] rounded-[10px] mb-4">
              <h2 className="headings-h2 text-zinc-950">{t("social_links.title")}</h2>
              <p className="text-zinc-500 text-sm mt-1">
                {t("social_links.subtitle")}
              </p>
            </div>

            <div className="edit-profile-form-bg p-[20px] bg-white rounded-[10px] border border-gray-100 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-6">
                {SOCIAL_PLATFORMS.map((platform) => (
                  <div key={platform.key} className="form-group mb-0">
                    <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                      <img src={platform.icon} alt={platform.label} className="w-5 h-5 object-contain" />
                      {t(platform.label)}
                    </label>
                    <Field
                      type="text"
                      name={platform.key}
                      placeholder={t(platform.placeholder)}
                      className="form-control-custom w-full"
                    />
                    <ErrorMessage
                      name={platform.key}
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-gray-100">
                <button
                  type="submit"
                  className="btn btn-pri min-w-[181px]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("social_links.updating") : t("btn_save")}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default SocialLinks;
